import {
  CAP_KIND_SET,
  type CapFilter,
  type CapKind,
  type GrantedCap,
} from "./capabilities.js";
import {
  expectArray,
  expectNonEmptyString,
  expectNumber,
  expectOneOf,
  expectRecord,
  expectString,
  isRecord,
  type JsonValue,
  ProtocolError,
  toJson,
} from "./json.js";
import {
  APP_MESSAGE_TAGS,
  type AppMessage,
  isPortRef,
  LOG_LEVELS,
  type LogLevel,
  type PortLike,
  type PortRef,
  SHELL_MESSAGE_TAGS,
  type ShellMessage,
  type Wire,
  type WindowInfo,
} from "./messages.js";
import { NODE_LIMITS, NODE_TYPE_SET, type Node, type NodeType } from "./nodes.js";

/* ------------------------------------------------------------------ nodes */

/** Validate a tree and return its JSON-safe form. Throws `ProtocolError`. */
export function encodeNode(node: Node): JsonValue {
  return encodeNodeAt(node, "tree", 0, { count: 0 });
}

function encodeNodeAt(
  node: Node,
  path: string,
  depth: number,
  budget: { count: number },
): JsonValue {
  if (depth > NODE_LIMITS.maxDepth) {
    throw new ProtocolError(`tree deeper than ${NODE_LIMITS.maxDepth}`, path);
  }
  if (++budget.count > NODE_LIMITS.maxNodes) {
    throw new ProtocolError(`tree larger than ${NODE_LIMITS.maxNodes} nodes`, path);
  }
  const record = expectRecord(node, path);
  const type = expectOneOf<NodeType>(record["type"], NODE_TYPE_SET, `${path}.type`);
  const key = expectNonEmptyString(record["key"], `${path}.key`);
  const props = toJson(expectRecord(record["props"], `${path}.props`), `${path}.props`);

  const rawChildren = record["children"];
  if (rawChildren === undefined) return { type, key, props };

  const children = expectArray(rawChildren, `${path}.children`);
  const seen = new Set<string>();
  const encoded = children.map((child, i) => {
    const childPath = `${path}.children[${i}]`;
    const childKey = isRecord(child) ? child["key"] : undefined;
    if (typeof childKey === "string") {
      if (seen.has(childKey)) {
        throw new ProtocolError(`duplicate sibling key ${JSON.stringify(childKey)}`, childPath);
      }
      seen.add(childKey);
    }
    return encodeNodeAt(child as Node, childPath, depth + 1, budget);
  });
  return { type, key, props, children: encoded };
}

/** Parse an untrusted value into a `Node`. Throws `ProtocolError`. */
export function decodeNode(value: unknown): Node {
  return decodeNodeAt(value, "tree", 0, { count: 0 });
}

function decodeNodeAt(
  value: unknown,
  path: string,
  depth: number,
  budget: { count: number },
): Node {
  if (depth > NODE_LIMITS.maxDepth) {
    throw new ProtocolError(`tree deeper than ${NODE_LIMITS.maxDepth}`, path);
  }
  if (++budget.count > NODE_LIMITS.maxNodes) {
    throw new ProtocolError(`tree larger than ${NODE_LIMITS.maxNodes} nodes`, path);
  }
  const record = expectRecord(value, path);
  const type = expectOneOf<NodeType>(record["type"], NODE_TYPE_SET, `${path}.type`);
  const key = expectNonEmptyString(record["key"], `${path}.key`);
  const props = expectRecord(record["props"], `${path}.props`);

  const rawChildren = record["children"];
  if (rawChildren === undefined) return { type, key, props };

  const children = expectArray(rawChildren, `${path}.children`);
  const seen = new Set<string>();
  const decoded = children.map((child, i) => {
    const node = decodeNodeAt(child, `${path}.children[${i}]`, depth + 1, budget);
    if (seen.has(node.key)) {
      throw new ProtocolError(
        `duplicate sibling key ${JSON.stringify(node.key)}`,
        `${path}.children[${i}]`,
      );
    }
    seen.add(node.key);
    return node;
  });
  return { type, key, props, children: decoded };
}

/* ------------------------------------------------------------- app → shell */

export function encodeAppMessage(message: AppMessage): Wire {
  switch (message.t) {
    case "render":
      return { body: { t: "render", tree: encodeNode(message.tree) }, transfer: [] };
    case "title":
      return { body: { t: "title", title: expectString(message.title, "title") }, transfer: [] };
    case "requestCapability": {
      const body: Record<string, unknown> = {
        t: "requestCapability",
        id: expectNonEmptyString(message.id, "id"),
        kind: expectOneOf<CapKind>(message.kind, CAP_KIND_SET, "kind"),
      };
      if (message.filter !== undefined) body["filter"] = toJson(message.filter, "filter");
      return { body, transfer: [] };
    }
    case "log":
      return {
        body: {
          t: "log",
          level: expectOneOf<LogLevel>(message.level, LOG_LEVELS, "level"),
          msg: expectString(message.msg, "msg"),
        },
        transfer: [],
      };
    default:
      throw new ProtocolError(
        `unknown app message ${JSON.stringify((message as { t: unknown }).t)}`,
        "",
      );
  }
}

export function decodeAppMessage(body: unknown): AppMessage {
  const record = expectRecord(body, "");
  const t = expectOneOf<AppMessage["t"]>(record["t"], APP_MESSAGE_TAGS, "t");
  switch (t) {
    case "render":
      return { t, tree: decodeNode(record["tree"]) };
    case "title":
      return { t, title: expectString(record["title"], "title") };
    case "requestCapability": {
      const id = expectNonEmptyString(record["id"], "id");
      const kind = expectOneOf<CapKind>(record["kind"], CAP_KIND_SET, "kind");
      const rawFilter = record["filter"];
      if (rawFilter === undefined) return { t, id, kind };
      return { t, id, kind, filter: decodeCapFilter(rawFilter, "filter") };
    }
    case "log":
      return {
        t,
        level: expectOneOf<LogLevel>(record["level"], LOG_LEVELS, "level"),
        msg: expectString(record["msg"], "msg"),
      };
  }
}

function decodeCapFilter(value: unknown, path: string): CapFilter {
  const record = expectRecord(value, path);
  const filter: { objectTypes?: readonly string[]; purpose?: string } = {};
  const rawTypes = record["objectTypes"];
  if (rawTypes !== undefined) {
    filter.objectTypes = expectArray(rawTypes, `${path}.objectTypes`).map((item, i) =>
      expectNonEmptyString(item, `${path}.objectTypes[${i}]`),
    );
  }
  const rawPurpose = record["purpose"];
  if (rawPurpose !== undefined) filter.purpose = expectString(rawPurpose, `${path}.purpose`);
  return filter;
}

/* ------------------------------------------------------------- shell → app */

export function encodeShellMessage(message: ShellMessage): Wire {
  switch (message.t) {
    case "mount": {
      const transfer: MessagePort[] = [];
      const capabilities = message.capabilities.map((cap, i) =>
        encodeGrantedCap(cap, transfer, `capabilities[${i}]`),
      );
      return {
        body: { t: "mount", window: encodeWindowInfo(message.window), capabilities },
        transfer,
      };
    }
    case "event": {
      const body: Record<string, unknown> = {
        t: "event",
        key: expectNonEmptyString(message.key, "key"),
        event: expectNonEmptyString(message.event, "event"),
      };
      if (message.payload !== undefined) body["payload"] = toJson(message.payload, "payload");
      return { body, transfer: [] };
    }
    case "capabilityGranted":
      return {
        body: {
          t: "capabilityGranted",
          id: expectNonEmptyString(message.id, "id"),
          port: portRef(0),
        },
        transfer: [message.port],
      };
    case "capabilityDenied":
      return {
        body: { t: "capabilityDenied", id: expectNonEmptyString(message.id, "id") },
        transfer: [],
      };
    case "unmount":
      return { body: { t: "unmount" }, transfer: [] };
    default:
      throw new ProtocolError(
        `unknown shell message ${JSON.stringify((message as { t: unknown }).t)}`,
        "",
      );
  }
}

export function decodeShellMessage(body: unknown, ports: readonly MessagePort[]): ShellMessage {
  const record = expectRecord(body, "");
  const t = expectOneOf<ShellMessage["t"]>(record["t"], SHELL_MESSAGE_TAGS, "t");
  switch (t) {
    case "mount": {
      const capabilities = expectArray(record["capabilities"], "capabilities").map((cap, i) =>
        decodeGrantedCap(cap, ports, `capabilities[${i}]`),
      );
      return { t, window: decodeWindowInfo(record["window"], "window"), capabilities };
    }
    case "event": {
      const key = expectNonEmptyString(record["key"], "key");
      const event = expectNonEmptyString(record["event"], "event");
      const payload = record["payload"];
      if (payload === undefined) return { t, key, event };
      return { t, key, event, payload };
    }
    case "capabilityGranted":
      return {
        t,
        id: expectNonEmptyString(record["id"], "id"),
        port: resolvePort(record["port"], ports, "port"),
      };
    case "capabilityDenied":
      return { t, id: expectNonEmptyString(record["id"], "id") };
    case "unmount":
      return { t };
  }
}

function encodeWindowInfo(info: WindowInfo): Record<string, unknown> {
  return {
    id: expectNonEmptyString(info.id, "window.id"),
    appId: expectNonEmptyString(info.appId, "window.appId"),
    w: expectNumber(info.w, "window.w"),
    h: expectNumber(info.h, "window.h"),
  };
}

function decodeWindowInfo(value: unknown, path: string): WindowInfo {
  const record = expectRecord(value, path);
  return {
    id: expectNonEmptyString(record["id"], `${path}.id`),
    appId: expectNonEmptyString(record["appId"], `${path}.appId`),
    w: expectNumber(record["w"], `${path}.w`),
    h: expectNumber(record["h"], `${path}.h`),
  };
}

function encodeGrantedCap(
  cap: GrantedCap,
  transfer: MessagePort[],
  path: string,
): Record<string, unknown> {
  const index = transfer.push(cap.port) - 1;
  const body: Record<string, unknown> = {
    id: expectNonEmptyString(cap.id, `${path}.id`),
    kind: expectOneOf<CapKind>(cap.kind, CAP_KIND_SET, `${path}.kind`),
    port: portRef(index),
  };
  if (cap.objectType !== undefined) {
    body["objectType"] = expectNonEmptyString(cap.objectType, `${path}.objectType`);
  }
  if (cap.label !== undefined) body["label"] = expectString(cap.label, `${path}.label`);
  return body;
}

function decodeGrantedCap(
  value: unknown,
  ports: readonly MessagePort[],
  path: string,
): GrantedCap {
  const record = expectRecord(value, path);
  const cap: GrantedCap = {
    id: expectNonEmptyString(record["id"], `${path}.id`),
    kind: expectOneOf<CapKind>(record["kind"], CAP_KIND_SET, `${path}.kind`),
    port: resolvePort(record["port"], ports, `${path}.port`),
  };
  const objectType = record["objectType"];
  if (objectType !== undefined) {
    cap.objectType = expectNonEmptyString(objectType, `${path}.objectType`);
  }
  const label = record["label"];
  if (label !== undefined) cap.label = expectString(label, `${path}.label`);
  return cap;
}

function portRef(index: number): PortRef {
  return { $port: index };
}

function resolvePort(
  value: unknown,
  ports: readonly MessagePort[],
  path: string,
): MessagePort {
  if (!isPortRef(value)) throw new ProtocolError("expected a port reference", path);
  const port = ports[value.$port];
  if (port === undefined) {
    throw new ProtocolError(`no transferred port at index ${value.$port}`, path);
  }
  return port;
}

/* --------------------------------------------------------------- transport */

export function sendAppMessage(port: PortLike, message: AppMessage): void {
  const wire = encodeAppMessage(message);
  port.postMessage(wire.body, wire.transfer);
}

export function sendShellMessage(port: PortLike, message: ShellMessage): void {
  const wire = encodeShellMessage(message);
  port.postMessage(wire.body, wire.transfer);
}

export function readAppMessage(event: MessageEvent): AppMessage {
  return decodeAppMessage(event.data);
}

export function readShellMessage(event: MessageEvent): ShellMessage {
  return decodeShellMessage(event.data, event.ports);
}
